import {Box, Grid, Typography} from '@mui/material';
import {
  getGridDateOperators,
  GridColDef,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  exportTagReport,
  getTagAuditReports,
} from '../../../api/EcaManagementService';
import {ReactComponent as BackArrow} from '../../../assests/svg/back-arrow-icon.svg';
import {ReactComponent as ExportIcon} from '../../../assests/svg/export_icon.svg';
import {TABLE_PAGE_SIZE_OPTION} from '../../../constants/constants';
import {RootState} from '../../../redux/store';
import {
  downloadResponseAsCSV,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import AurButton from '../../common/aur-button/AurButton';
import AurDataGridPro from '../../common/aur-data-grid-pro/AurDataGridPro';
import {toODataQueryResult} from '../../common/aur-data-grid-pro/useServerSideDataGrid';
import AurDataGridCellWrapper from '../../common/aur-data-grid/AurDataGridCellWrapper';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import AurInfographicSeperator from '../../common/aur-infographic/AurInfographicSeperator';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurPagination from '../../common/aur-pagination/AurPagination';
import FieldPopoverGridFilter, {
  FieldFilterGridData,
} from '../field-management/field-table/FieldPopoverGridFilter';
import AurDataGridLoadingOverlay from '../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import dayjs from 'dayjs';
import {debounce} from 'lodash';
import {getFieldCategoryList} from '../../../utils/fieldManagementUtils';
// Defining types for rows
interface ReportRow {
  id: number;
  fieldName: string;
  tagName: string;
  actionType: string;
  documentCount: number;
  actionPerformedBy: string;
  dateTime: string;
}

const TABLE_BASE_WIDTH = 1328;

const TaggingAuditReport = () => {
  const [totalItems, setTotalItems] = useState(100);
  const [itemsPerPage, setItemsPerPage] = useState(TABLE_PAGE_SIZE_OPTION[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCount, setFilterCount] = useState<number>(0);
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const initialLoad = useRef(false);
  const lastODataQueryTagAudit = useRef<string | null>(null);
  const apiRef = useGridApiRef();

  const fieldTypeDataArray: FieldFilterGridData[] = [
    {
      id: 1,
      label: t('common.tagged'),
    },
    {
      id: 2,
      label: t('common.untagged'),
    },
  ];

  const renderEmptyHeaderFilter = (label: string) => (
    <span className="sr-only">{label}</span>
  );

  const columns: GridColDef<ReportRow>[] = [
    {
      field: 'id',
      headerName: '#',
      flex: 48 / TABLE_BASE_WIDTH,
      filterable: false,
      sortable: false,
      resizable: false,
      renderHeaderFilter: () =>
        renderEmptyHeaderFilter(
          `${t('common.rowNumber') + ' ' + t('common.filterNotAvailable')}`
        ),
    },
    {
      field: 'fieldName',
      headerName: t('tagging-audit-report.fieldName'),
      flex: 243 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
    },
    {
      field: 'tagName',
      headerName: t('tagging-audit-report.tagName'),
      flex: 161 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
    },
    {
      field: 'actionType',
      headerName: t('tagging-audit-report.actionType'),
      flex: 277 / TABLE_BASE_WIDTH,
      sortable: false,
      resizable: false,
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'fieldESType'}
          apiRef={apiRef}
          columnField={'actionType'}
          type={'options'}
          dataArray={fieldTypeDataArray}
        />
      ),
    },
    {
      field: 'documentCount',
      headerName: t('tagging-audit-report.documentCount'),
      flex: 169 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      maxWidth: 169,
      renderHeaderFilter: () =>
        renderEmptyHeaderFilter(
          `${t('tagging-audit-report.documentCount') + ' ' + t('common.filterNotAvailable')}`
        ),
    },
    {
      field: 'actionPerformedBy',
      headerName: t('tagging-audit-report.actionPerformedBy'),
      flex: 209 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
    },
    {
      field: 'dateTime',
      headerName: t('tagging-audit-report.datesTime'),
      flex: 221 / TABLE_BASE_WIDTH,
      sortable: false,
      resizable: false,
      type: 'date',
      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (_value, row) => {
        return getUTCDateTimeForDisplay(row.dateTime);
      },
      renderCell: (param) => {
        return (
          <AurDataGridCellWrapper
            tooltipText={t('common.yearMonthDate')}
            cellData={param.formattedValue}
          />
        );
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={apiRef}
          columnField={'dateTime'}
          type={'date'}
          dataArray={getFieldCategoryList()}
        />
      ),
    },
  ];

  const onPageChange = (page: any) => {
    setCurrentPage(page);
  };
  const handlePageSizeChange = (pageSize: number) => {
    if (pageSize === itemsPerPage) return;
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  const [isClearAllLoading, setIsClearAllLoading] = useState<boolean>(false);
  const {projectId, selectedProjectName} = useSelector(
    (state: RootState) => state.reports
  );
  const {clientId} = useSelector((state: RootState) => state?.selectedClient);
  const clientName = useSelector((state: RootState) => state?.selectedClient);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const fetchAuditReport = useCallback(
    async (ODataQuery: string) => {
      if (lastODataQueryTagAudit.current === ODataQuery) return; // ✅ Prevent duplicate calls
      lastODataQueryTagAudit.current = ODataQuery;
      setLoading(true);
      try {
        const paramString = `?clientId=${clientId}&projectId=${projectId}&PageNumber=${currentPage}&PageSize=${itemsPerPage}&${ODataQuery}`;

        //const paramString = `clientId=${clientId}&projectId=${projectId}&PageNumber=${currentPage}&PageSize=${itemsPerPage}&${ODataQuery}`;
        const response = await getTagAuditReports(paramString);
        const res = response.data;
        const data: ReportRow[] = res?.data || [];

        setTotalItems(res?.meta?.pagination?.totalItems);
        const rowsWithId: ReportRow[] = data.map((row, index) => ({
          ...row,
          id: index + (currentPage - 1) * itemsPerPage + 1,
        }));

        setReportData(rowsWithId);
      } catch (error) {
        console.error('Error fetching audit report:', error);
      } finally {
        setLoading(false);
      }
    },
    [clientId, currentPage, itemsPerPage, projectId]
  );

  const fetchTagReport = async () => {
    setIsClearAllLoading(true);
    try {
      const paramString = `clientId=${clientId}&projectId=${projectId}`;
      const response = await exportTagReport(paramString);
      const formattedDate = Date.now();
      downloadResponseAsCSV(response, `${formattedDate}`);
    } catch (error) {
      //
    } finally {
      setIsClearAllLoading(false);
    }
  };

  const fetchData = useCallback(() => {
    if (!apiRef?.current || !projectId || !clientId) return;

    apiRef.current.state.filter.filterModel.items.map((item) => {
      if (item.field === 'dateTime') {
        item.value = dayjs(item.value).format('YYYY-MM-DD');
      }
      return item;
    });

    const filterModel = apiRef.current.state.filter.filterModel;
    const sortModel = apiRef.current.state.sorting.sortModel;
    const uniqueFields = new Set(filterModel.items.map((item) => item.field));
    setFilterCount(uniqueFields.size);
    const ODataQuery = toODataQueryResult(filterModel, sortModel, {
      page: currentPage,
      pageSize: itemsPerPage,
    });
    const updatedQuery = ODataQuery.replace(/isAnyOf/g, 'in');
    fetchAuditReport(updatedQuery);
  }, [
    apiRef,
    projectId,
    clientId,
    currentPage,
    itemsPerPage,
    fetchAuditReport,
  ]);

  useEffect(() => {
    if (!initialLoad.current) {
      initialLoad.current = true;
      fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    if (initialLoad.current) {
      fetchData();
    }
  }, [currentPage, itemsPerPage, fetchData]);

  useEffect(() => {
    if (!apiRef?.current) return;

    const debouncedFetchData = debounce(() => {
      setCurrentPage(1);
      fetchData();
    }, 500);
    const handleFilterChange = () => {
      debouncedFetchData();
    };
    const unsubscribe = apiRef.current.subscribeEvent(
      'filterModelChange',
      handleFilterChange
    );
    return () => {
      unsubscribe();
      debouncedFetchData.cancel();
    };
  }, [apiRef, fetchData]);

  const goBack = () => {
    navigate(-1);
  };

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };
  const noDataGridOverlay = useMemo(() => {
    if (loading) return AurDataGridLoadingOverlay;
    if (filterCount > 0 && !(reportData.length > 0))
      return AurDataGridNoRowsOverlay;
    return AurDataGridNoRowsOverlay;
  }, [
    filterCount,
    loading,
    reportData.length,
  ]) as React.JSXElementConstructor<unknown>;

  const renderPaginationBlock = (position: 'top' | 'bottom') => {
    return (
      <Box className={'table-pagination-container'}>
        <Box className={'table-pagination-control'}>
          <AurPagination
            totalItems={totalItems}
            itemType={t('fieldManagement.fieldTable.events')}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            perPageOptions={TABLE_PAGE_SIZE_OPTION}
            onPageChange={onPageChange}
            setItemsPerPage={handlePageSizeChange}
            paginationAriaLabel={
              position === 'top'
                ? t('common.pagination.topPagination', {
                    tableName: t('tagging-audit-report.title'),
                  })
                : t('common.pagination.bottomPagination', {
                    tableName: t('tagging-audit-report.title'),
                  })
            }
          />
        </Box>
        {filterCount > 0 ? (
          <>
            <Box>
              <AurInfographicSeperator seperatorIcon={'VerticalLine'} />
            </Box>
            <Box
              className={'apply-filter-button-container'}
              onClick={() => {
                apiRef.current?.setFilterModel({items: []});
              }}
            >
              <AurButton
                type={'exit'}
                size={'Small'}
                startIcon={<AurIcon icon={IconNames.ClearFiltersIcon} />}
                label={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
              />
            </Box>
          </>
        ) : null}
      </Box>
    );
  };

  return (
    <div className="tag-report-screen">
      <Box className={'aur-field-table-container'}>
        <Typography
          className="typo-text body2_size back-button"
          onClick={goBack}
          tabIndex={0}
        >
          <BackArrow /> {t('tagging-audit-report.backText')}
        </Typography>
        <div className="header-text">
          <Typography className="typo-text body1_size" tabIndex={0}>
            {clientName?.name}
          </Typography>
          <Typography className="typo-text display3_size" tabIndex={0}>
            {selectedProjectName}
          </Typography>
        </div>
        <div className="body-area">
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            className="export-area"
          >
            <Grid item xs={8}>
              <Typography
                className="typo-text subhead1_size"
                tabIndex={0}
                component="h1"
              >
                {t('tagging-audit-report.title')}
              </Typography>
              <Typography className="typo-text body2_size">
                {t('tagging-audit-report.description')}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <AurButton
                dataTestId="export-button"
                onClick={() => fetchTagReport()}
                startIcon={
                  isClearAllLoading ? (
                    <div className="color-loader">
                      <AurLoadingIndicator
                        size={16}
                        className="progress-icon"
                        aria-hidden="true"
                      />
                    </div>
                  ) : (
                    <ExportIcon aria-hidden="true" />
                  )
                }
                type="continue"
                size="Medium"
                label={t('tagging-audit-report.exportButtonText')}
                classNames="typo-text buttonMedium_size"
              />
            </Grid>
          </Grid>

          <div className="eca-searchterm-report tagging-report-screen">
            <div className="eca-searchterm-report-table">
              <div className="data-table-container">
                <div className="total-content">
                  <Typography className="typo-text subhead2_size" tabIndex={0}>
                    {t('tagging-audit-report.totalEvents')}
                  </Typography>
                  <Typography className="count-text" tabIndex={0}>
                    ({totalItems})
                  </Typography>
                </div>
                {renderPaginationBlock('top')}
                <AurDataGridPro
                  className="aur-grid-pro"
                  apiRef={apiRef}
                  rows={reportData}
                  columns={columns}
                  hideFooter
                  headerFilters
                  disableRowSelectionOnClick
                  enableServerSide
                  noRowOverlayClass="no-row-container"
                  data-testid="str-table"
                  checkBoxVisible={false}
                  defaultItemsPerPage={itemsPerPage}
                  columnHeaderHeight={48}
                  headerFilterHeight={48}
                  rowHeight={36}
                  sortModel={sortModel}
                  setSortModel={onSetSortModel}
                  slots={{
                    headerFilterMenu: null,
                    noRowsOverlay: noDataGridOverlay,
                  }}
                  slotProps={{
                    headerFilterCell: {
                      showClearIcon: false,
                      InputComponentProps: {
                        type: 'search',
                        label: '',
                        placeholder: t('fieldManagement.fieldTable.search'),
                        autoComplete: 'off',
                        tabIndex: 0,
                        inputProps: {
                          tabIndex: 0,
                          'aria-label': t('fieldManagement.fieldTable.search'),
                        },
                      },
                    },
                  }}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                />
                {renderPaginationBlock('bottom')}
              </div>
            </div>
          </div>
        </div>
      </Box>
    </div>
  );
};

export default TaggingAuditReport;
